<script setup lang="ts">
import type Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import {
  computed,
  ref,
  toRef,
} from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { useRestClient } from '@modules/core/services/api';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { TRANSLATION_GROUP_FILE_UPLOAD } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  EXPORT_TRANSLATIONS_URL,
  TRANSLATE_MANAGER_PERMISSIONS,
  TRANSLATION_GROUP_TRANSLATE_MANAGER,
} from '@modules/translate-manager/constants';
import { useImportTranslationsMutation } from '@modules/translate-manager/mutations';
import convertTreeListToYmlSyntax from '@modules/translate-manager/pages/TranslateManagerPage/composables/utils/convertTreeListToYmlSyntax';
import createAndDownloadFile from '@modules/translate-manager/pages/TranslateManagerPage/composables/utils/createAndDownloadFile';

import type {
  ITranslationLoaderEmits,
  ITranslationLoaderProps,
  ITranslationUploadData,
} from './types';

const props = defineProps<ITranslationLoaderProps>();
const emit = defineEmits<ITranslationLoaderEmits>();
const language = toRef(props, 'language');

const { getTranslation } = useI18n();
const { openLink } = useRestClient();
const {
  isLoading,
  mutateAsync,
  errors,
  clearError,
  clearAllErrors,
} = useImportTranslationsMutation();

const menu = ref<InstanceType<typeof Menu>>();
const isDialogOpened = ref(false);
const forceLoad = ref(false);

function downloadTranslations(): void {
  if (props.isEmptyLocalesEnabled) {
    createAndDownloadFile(convertTreeListToYmlSyntax(props.unitedTreeTranslations), 'application/x-yaml', `empty_translation_keys_${language.value}.yml`);
    return;
  }

  openLink(EXPORT_TRANSLATIONS_URL, {
    permission: TRANSLATE_MANAGER_PERMISSIONS.TRANSLATEMANAGER_EDIT,
    queryParams: {
      language: language.value,
    },
  });
}

function toggle(event: MouseEvent): void {
  menu.value?.toggle(event);
}

async function onSubmit({ file }: ITranslationUploadData): Promise<void> {
  const payload = new FormData();
  payload.append('language', language.value);
  payload.append('force_load', String(forceLoad.value));
  payload.append('file', file);

  try {
    await mutateAsync(payload);

    isDialogOpened.value = false;
    emit('uploaded');
  } catch {}
}

function openDialog(): void {
  clearAllErrors();
  isDialogOpened.value = true;
}

const options = computed<MenuItem[]>(() => ([
  {
    label: getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'FORCE_WRITE').value,
    icon: 'pi pi-cloud-upload',
    command: (): void => {
      forceLoad.value = true;
      openDialog();
    },
  },
  {
    label: getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'WRITE_ONLY_EMPTY').value,
    icon: 'pi pi-file-import',
    command: (): void => {
      forceLoad.value = false;
      openDialog();
    },
  },
]));

const uploadSchema = computed<FormSchema<ITranslationUploadData>>(() => ({
  file: {
    type: EFormInputTypes.SINGLE_FILE,
    format: 'yml',
    accept: '.yml,.yaml',
    disabled: isLoading.value,
    showUploadButton: true,
  },
}));

function onHide(): void {
  isDialogOpened.value = false;
}
</script>

<template>
  <div v-data-qa-auto-id="'VTranslationLoader'" class="flex gap-2">
    <VButton
      type="button"
      :label="$getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'EXPORT_FILE_BUTTON')"
      :loading="isLocalesLoading || isLoading"
      icon="pi pi-download"
      @click="downloadTranslations"
    />
    <VButton
      :label="$getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'IMPORT_FILE_BUTTON')"
      :loading="isLocalesLoading || isLoading"
      aria-haspopup="true"
      aria-controls="upload_menu"
      @click="toggle"
    />
    <PMenu
      id="upload_menu"
      ref="menu"
      class="w-72"
      :model="options"
      popup
    />
  </div>
  <VDialog
    :visible="isDialogOpened"
    :title="$getTranslation(TRANSLATION_GROUP_FILE_UPLOAD, 'TITLE')"
    @hide="onHide()"
  >
    <VForm
      :schema="uploadSchema"
      :custom-errors="errors"
      @submit="onSubmit"
      @interacted="clearError"
    />
  </VDialog>
</template>
